import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

/* ── MUI imports ──────────────────────────────────────────────── */
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import BoltIcon from "@mui/icons-material/Bolt";
import EditIcon from "@mui/icons-material/Edit";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";

import Langs from "../../app/lang/langs";
import {
    getProjectAptitudesWithValues,
    answerAptitudeDailyBase,
    setCollabProjectFormVisibility,
    setOnAnswerProject,
    setProjectAptitudes
} from "./vcompSlice";
import { showErrorToast } from "../../app/utils/helpers";

/* ── Constants ────────────────────────────────────────────────── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255, 144, 21, 0.08)";
const ORANGE_GLOW = "rgba(255, 144, 21, 0.25)";
const ORANGE_GLASS = "rgba(255, 144, 21, 0.06)";
const ORANGE_BORDER = "rgba(255, 144, 21, 0.12)";
const ORANGE_SOFT_GLOW = "0 0 20px rgba(255, 144, 21, 0.15), 0 0 40px rgba(255, 144, 21, 0.05)";
const GLASS_BG = "rgba(255, 255, 255, 0.72)";
const GLASS_BORDER = "rgba(255, 255, 255, 0.35)";
const GLASS_SHADOW = "0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.03)";
const EASE_SMOOTH = "cubic-bezier(0.4, 0, 0.2, 1)";
const EASE_ENTRANCE = "cubic-bezier(0, 0, 0.2, 1)";

/* ── Global keyframes (injected once via style tag) ────────── */
const KEYFRAMES_ID = "mcpf-keyframes";
const KEYFRAMES_CSS = `
@keyframes mcpf-fadeSlideUp {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes mcpf-stepFadeIn {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes mcpf-chipFadeIn {
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes mcpf-nodeFadeIn {
  from { opacity: 0; transform: translateX(-4px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes mcpf-progressGlow {
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.15); }
}
@keyframes mcpf-pulseRing {
  0%   { box-shadow: 0 0 0 0 rgba(255,144,21,0.3); }
  70%  { box-shadow: 0 0 0 8px rgba(255,144,21,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,144,21,0); }
}
@keyframes mcpf-counterPop {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.15); }
  100% { transform: scale(1); }
}
`;

/* ── Scrollbar mixin (thin orange-tinted) ────────────────────── */
const scrollbarSx = {
    '&::-webkit-scrollbar': {
        width: '5px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 144, 21, 0.18)',
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:active': {
        background: 'rgba(255, 144, 21, 0.35)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,144,21,0.18) transparent',
};

const PROGRESS_STEPS_PRACTICAL = [
    { label: "Ne sait pas encore ce que c'est", value: "0" },
    { label: "Expliqué", value: "1" },
    { label: "Vu (Double passif)", value: "2" },
    { label: "Réalisé avec accompagnement (Double Actif)", value: "3" },
    { label: "Réalisé sans aide (Double Actif)", value: "4" },
    { label: "Maîtrisé (Autonomie)", value: "5" }
];

const PROGRESS_STEPS_THEORETICAL = [
    { label: "Rien fait", value: "0" },
    { label: "Lu/Expliqué une fois (en partie)", value: "1" },
    { label: "Lu/Expliqué une fois (totalité)", value: "2" },
    { label: "Compris/Connu en partie", value: "3" },
    { label: "Compris/Connu en totalité", value: "4" },
    { label: "Maîtrisé (Autonome)", value: "5" }
];

/* ── Transition ───────────────────────────────────────────────── */
const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Color helper ─────────────────────────────────────────────── */
const getProgressColor = (num) => {
    if (num < 0 || num > 100) return '#9ca3af';
    if (num < 25) return '#ef4444';
    if (num < 50) return ORANGE;
    if (num < 75) return '#eab308';
    return '#22c55e';
};

/* ── Mobile Progress Tree Node ────────────────────────────────── */
const MobileProgressNode = ({ node, depth = 0, expandedKeys, onToggle, nodeIndex = 0 }) => {
    const isExpandable = node.children && node.children.length > 0;
    const isExpanded = expandedKeys[node.key];
    const color = getProgressColor(node.progress);
    const progressValue = Math.min(Math.max(node.progress || 0, 0), 100);

    return (
        <Box sx={{
            animation: `mcpf-nodeFadeIn 350ms ${EASE_ENTRANCE} ${nodeIndex * 30}ms both`,
        }}>
            <Box
                onClick={() => isExpandable && onToggle(node.key)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1.25,
                    px: 2,
                    pl: 2 + depth * 2,
                    mx: depth === 0 ? 1.5 : 0,
                    mt: depth === 0 ? 0.75 : 0,
                    borderRadius: depth === 0 ? '14px' : '10px',
                    backgroundColor: depth === 0
                        ? GLASS_BG
                        : isExpandable
                            ? 'rgba(255, 255, 255, 0.5)'
                            : 'transparent',
                    backdropFilter: depth === 0 ? 'blur(12px)' : 'none',
                    WebkitBackdropFilter: depth === 0 ? 'blur(12px)' : 'none',
                    border: depth === 0 ? `1px solid ${GLASS_BORDER}` : 'none',
                    borderBottom: depth === 0 ? `1px solid ${GLASS_BORDER}` : '1px solid rgba(0,0,0,0.03)',
                    boxShadow: depth === 0 ? GLASS_SHADOW : 'none',
                    cursor: isExpandable ? 'pointer' : 'default',
                    transition: `all 250ms ${EASE_SMOOTH}`,
                    '&:active': isExpandable ? {
                        backgroundColor: depth === 0 ? 'rgba(255, 144, 21, 0.06)' : 'rgba(0,0,0,0.02)',
                        transform: 'scale(0.99)',
                    } : {},
                }}
            >
                {/* Expand/collapse icon */}
                {isExpandable ? (
                    <Box sx={{
                        width: 26,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        borderRadius: '8px',
                        backgroundColor: isExpanded ? ORANGE_LIGHT : 'rgba(0,0,0,0.04)',
                        transition: `all 300ms ${EASE_SMOOTH}`,
                    }}>
                        <Box sx={{
                            display: 'flex',
                            transition: `transform 300ms ${EASE_SMOOTH}`,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}>
                            <ExpandMoreIcon sx={{
                                fontSize: 18,
                                color: isExpanded ? ORANGE : '#9ca3af',
                                transition: `color 250ms ${EASE_SMOOTH}`,
                            }} />
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: color,
                        opacity: 0.5,
                        flexShrink: 0,
                        ml: 1.25,
                        mr: 1,
                    }} />
                )}

                {/* Name + aspect badge */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{
                            fontSize: depth === 0 ? '0.9rem' : '0.85rem',
                            fontWeight: isExpandable ? 600 : 500,
                            color: '#2d3748',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            letterSpacing: '-0.01em',
                        }}>
                            {node.label}
                        </Typography>
                        {node.aspect === 'theoretical' && (
                            <Box component="span" sx={{
                                fontSize: '0.6rem', fontWeight: 700, color: '#6366f1',
                                background: 'rgba(99, 102, 241, 0.1)', px: 0.75, py: 0.25,
                                borderRadius: '6px', letterSpacing: '0.02em', flexShrink: 0,
                                border: '1px solid rgba(99, 102, 241, 0.08)',
                            }}>[T]</Box>
                        )}
                        {node.aspect === 'practical' && (
                            <Box component="span" sx={{
                                fontSize: '0.6rem', fontWeight: 700, color: ORANGE,
                                background: ORANGE_LIGHT, px: 0.75, py: 0.25,
                                borderRadius: '6px', letterSpacing: '0.02em', flexShrink: 0,
                                border: `1px solid ${ORANGE_BORDER}`,
                            }}>[P]</Box>
                        )}
                    </Box>
                </Box>

                {/* Progress bar + percentage */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 105, maxWidth: 140 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            boxShadow: progressValue > 0 ? `0 0 8px ${color}22` : 'none',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: color,
                                borderRadius: 3,
                                boxShadow: progressValue > 20 ? `0 1px 6px ${color}44` : 'none',
                                transition: `transform 600ms ${EASE_SMOOTH}`,
                            },
                        }}
                    />
                    <Typography sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: color,
                        minWidth: 36,
                        textAlign: 'right',
                        letterSpacing: '-0.02em',
                    }}>
                        {parseFloat(progressValue).toFixed(1)}%
                    </Typography>
                </Box>
            </Box>

            {/* Children */}
            {isExpandable && (
                <Collapse in={isExpanded} timeout={300} easing={EASE_SMOOTH}>
                    {node.children.map((child, childIdx) => (
                        <MobileProgressNode
                            key={child.key}
                            node={child}
                            depth={depth + 1}
                            expandedKeys={expandedKeys}
                            onToggle={onToggle}
                            nodeIndex={childIdx}
                        />
                    ))}
                </Collapse>
            )}
        </Box>
    );
};

/* ── Mobile Step Card ─────────────────────────────────────────── */
const MobileStepCard = ({ step, isSelected, isDisabled, isCompleted, onSelect, index }) => (
    <Box
        onClick={() => !isDisabled && onSelect(step.value)}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            minHeight: 56,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: isSelected
                ? 'rgba(255, 144, 21, 0.3)'
                : isDisabled
                    ? 'rgba(0,0,0,0.03)'
                    : 'rgba(255, 255, 255, 0.4)',
            backgroundColor: isSelected
                ? 'rgba(255, 144, 21, 0.08)'
                : isDisabled
                    ? 'rgba(0,0,0,0.015)'
                    : GLASS_BG,
            backdropFilter: isDisabled ? 'none' : 'blur(12px)',
            WebkitBackdropFilter: isDisabled ? 'none' : 'blur(12px)',
            boxShadow: isSelected
                ? `0 0 0 1px rgba(255,144,21,0.1), ${ORANGE_SOFT_GLOW}`
                : isDisabled
                    ? 'none'
                    : '0 2px 8px rgba(0,0,0,0.03), 0 1px 2px rgba(0,0,0,0.02)',
            opacity: isDisabled ? 0.45 : 1,
            cursor: isDisabled ? 'default' : 'pointer',
            transition: `all 250ms ${EASE_SMOOTH}`,
            transform: 'scale(1) translateZ(0)',
            '&:active': !isDisabled ? {
                transform: 'scale(0.98) translateZ(0)',
                boxShadow: isSelected
                    ? `0 0 0 1px rgba(255,144,21,0.15), 0 0 12px rgba(255,144,21,0.1)`
                    : '0 1px 4px rgba(0,0,0,0.04)',
            } : {},
            animation: `mcpf-stepFadeIn 350ms ${EASE_ENTRANCE} ${index * 50}ms both`,
        }}
    >
        {/* Number badge */}
        <Box sx={{
            width: 34,
            height: 34,
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '0.8rem',
            fontWeight: 700,
            background: isSelected
                ? `linear-gradient(135deg, ${ORANGE}, #FFC100)`
                : isCompleted
                    ? 'linear-gradient(135deg, #22c55e, #4ade80)'
                    : 'rgba(0,0,0,0.05)',
            color: isSelected || isCompleted ? '#fff' : '#6b7280',
            boxShadow: isSelected
                ? '0 2px 8px rgba(255,144,21,0.3)'
                : isCompleted
                    ? '0 2px 8px rgba(34,197,94,0.25)'
                    : 'none',
            transition: `all 300ms ${EASE_SMOOTH}`,
        }}>
            {isCompleted && !isSelected
                ? <CheckCircleIcon sx={{ fontSize: 18, color: '#fff' }} />
                : step.value
            }
        </Box>

        {/* Label */}
        <Typography sx={{
            flex: 1,
            fontSize: '0.875rem',
            fontWeight: isSelected ? 600 : 500,
            color: isDisabled ? '#b0b7c3' : isSelected ? '#1a1a2e' : '#374151',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
            transition: `all 250ms ${EASE_SMOOTH}`,
        }}>
            {step.label}
        </Typography>

        {/* Check icon when selected */}
        {isSelected && (
            <Box sx={{
                animation: `mcpf-counterPop 350ms ${EASE_ENTRANCE}`,
            }}>
                <CheckCircleIcon sx={{
                    fontSize: 22,
                    color: ORANGE,
                    flexShrink: 0,
                    filter: 'drop-shadow(0 1px 3px rgba(255,144,21,0.3))',
                }} />
            </Box>
        )}
    </Box>
);

/* ── Main Component ───────────────────────────────────────────── */
const MobileCollabProjectForm = () => {
    const dispatch = useDispatch();
    const visible = useSelector((state) => state.vcomp.collabProjectFormVisibility);
    const projectAptitudes = useSelector((state) => state.vcomp.projectAptitudesWithValues);
    const onAnswerProject = useSelector((state) => state.vcomp.onAnswerProject);

    const [activeIndex, setActiveIndex] = useState(0);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [localApt, setLocalApt] = useState([]);
    const [selectedOption, setSelectedOption] = useState('');

    /* ── Progress tree state ── */
    const [progressNodes, setProgressNodes] = useState([]);
    const [overallProgress, setOverallProgress] = useState(0);
    const [expandedKeys, setExpandedKeys] = useState({});

    /* ── Swipe refs ── */
    const touchStartX = useRef(null);
    const contentRef = useRef(null);

    /* ── Inject keyframes once ── */
    useEffect(() => {
        if (!document.getElementById(KEYFRAMES_ID)) {
            const style = document.createElement('style');
            style.id = KEYFRAMES_ID;
            style.textContent = KEYFRAMES_CSS;
            document.head.appendChild(style);
        }
    }, []);

    /* ── Data fetching ── */
    useEffect(() => {
        if (onAnswerProject) {
            dispatch(getProjectAptitudesWithValues({ id_project: onAnswerProject.id }));
        }
    }, [onAnswerProject, dispatch]);

    /* ── Flatten aptitudes for entry tab ── */
    useEffect(() => {
        if (projectAptitudes && projectAptitudes.categories) {
            setLocalApt(flattenData(projectAptitudes.categories));
        } else {
            setLocalApt([]);
        }
    }, [projectAptitudes]);

    /* ── Process tree nodes for progress tab ── */
    useEffect(() => {
        if (projectAptitudes && projectAptitudes.categories && projectAptitudes.categories.length > 0) {
            const processNode = (node) => {
                const keyPrefix = node.scope === 'category' ? 'cat' :
                    node.scope === 'subcategory' ? 'subcat' : 'apt';
                const key = `${keyPrefix}-${node.id}`;
                let processedChildren = node.children
                    ? node.children.map(processNode).filter(child => child !== undefined)
                    : [];
                if ((keyPrefix === 'cat' || keyPrefix === 'subcat') && processedChildren.length === 0) {
                    return undefined;
                }
                return {
                    key,
                    id: node.id,
                    label: node.nom,
                    progress: node.progress * 20,
                    coef: node.coef,
                    className: node.scope,
                    pos: node.pos,
                    visibility: node.visibility,
                    description: node.description,
                    type: node.type,
                    aspect: node.aspect || null,
                    children: processedChildren,
                };
            };

            const sdata = projectAptitudes.categories.map(processNode).filter(s => s !== undefined);
            const recalculated = filterByCollabIdAndRecalculateScores(sdata);
            const totalCoef = recalculated.reduce((acc, cat) => acc + (cat.coef || 1), 0);
            const weightedSum = recalculated.reduce((acc, cat) => acc + ((cat.progress || 0) * (cat.coef || 1)), 0);
            const avgProgress = totalCoef > 0 ? weightedSum / totalCoef : 0;
            setOverallProgress(parseFloat(avgProgress).toFixed(2));
            setProgressNodes(recalculated);
        } else {
            setProgressNodes([]);
            setOverallProgress(0);
        }
    }, [projectAptitudes]);

    /* ── Sync selected option when active index changes ── */
    useEffect(() => {
        if (localApt[activeIndex]) {
            setSelectedOption(localApt[activeIndex].progress !== null ? localApt[activeIndex].progress.toString() : '');
        }
    }, [activeIndex, localApt]);

    /* ── Flatten data ── */
    const flattenData = (data) => {
        let aptitudes = [];
        const processAptitude = (aptitude, categoryId = null, categoryName = null, subcategoryId = null, subcategoryName = null) => {
            aptitudes.push({
                id: aptitude.id,
                nom: aptitude.nom,
                coef: aptitude.coef,
                visibility: aptitude.visibility,
                description: aptitude.description,
                progress: aptitude.progress,
                type: aptitude.type,
                pos: aptitude.pos,
                category_id: categoryId,
                category_name: categoryName,
                subcategory_id: subcategoryId,
                subcategory_name: subcategoryName,
                aspect: aptitude.aspect
            });
        };
        data.forEach(category => {
            if (category.scope === "category") {
                const categoryName = category.nom;
                const categoryId = category.id;
                if (category.children && category.children.length) {
                    category.children.forEach(item => {
                        if (item.scope === "subcategory") {
                            const subcategoryName = item.nom;
                            const subcategoryId = item.id;
                            item.children.forEach(aptitude => {
                                if (aptitude.scope === "aptitude") {
                                    processAptitude(aptitude, categoryId, categoryName, subcategoryId, subcategoryName);
                                }
                            });
                        } else if (item.scope === "aptitude") {
                            processAptitude(item, categoryId, categoryName);
                        }
                    });
                }
            } else if (category.scope === "aptitude") {
                processAptitude(category);
            }
        });
        return aptitudes;
    };

    /* ── Recalculate scores for tree ── */
    function filterByCollabIdAndRecalculateScores(data) {
        const dataClone = JSON.parse(JSON.stringify(data));
        function recurse(elements) {
            return elements.map(element => {
                if (element && element.children && element.children.length > 0) {
                    element.children = recurse(element.children);
                    const totalCoef = element.children.reduce((acc, child) => acc + child.coef, 0);
                    const weightedSum = element.children.reduce((acc, child) => acc + (child.progress * child.coef), 0);
                    const averageProgress = totalCoef > 0 ? weightedSum / totalCoef : 0;
                    element.progress = averageProgress;
                }
                return element;
            }).filter(element => element !== null);
        }
        return recurse(dataClone);
    }

    /* ── Collect all expandable keys ── */
    const collectAllKeys = (nodeList) => {
        let keys = {};
        (nodeList || []).forEach(node => {
            if (node.children && node.children.length > 0) {
                keys[node.key] = true;
                Object.assign(keys, collectAllKeys(node.children));
            }
        });
        return keys;
    };

    const handleToggleKey = useCallback((key) => {
        setExpandedKeys(prev => {
            const next = { ...prev };
            if (next[key]) {
                delete next[key];
            } else {
                next[key] = true;
            }
            return next;
        });
    }, []);

    /* ── Close handler ── */
    const handleClose = () => {
        dispatch(setCollabProjectFormVisibility(false));
        dispatch(setOnAnswerProject(null));
        dispatch(setProjectAptitudes([]));
        setActiveIndex(0);
        setActiveTabIndex(0);
    };

    /* ── Next handler ── */
    const handleNext = () => {
        if (localApt[activeIndex]) {
            let nextIndex = activeIndex;
            do {
                nextIndex++;
            } while (nextIndex < localApt.length && localApt[nextIndex].progress == "5");

            if (nextIndex < localApt.length && localApt[nextIndex]) {
                const currentApt = localApt[activeIndex];
                if (currentApt.progress !== selectedOption) {
                    try {
                        updateLocalData(activeIndex, selectedOption);
                        dispatch(answerAptitudeDailyBase({ id_aptitude: currentApt.id, progress: selectedOption }));
                        setSelectedOption('');
                    } catch (error) {
                        showErrorToast();
                        return;
                    }
                }
                setActiveIndex(nextIndex);
            } else {
                const currentApt = localApt[activeIndex];
                if (currentApt.progress !== selectedOption) {
                    try {
                        updateLocalData(activeIndex, selectedOption);
                        dispatch(answerAptitudeDailyBase({ id_aptitude: currentApt.id, progress: selectedOption }));
                        setSelectedOption('');
                    } catch (error) {
                        showErrorToast();
                        return;
                    }
                }
                handleClose();
            }
        }
    };

    /* ── Update local data ── */
    const updateLocalData = (index, progress) => {
        localApt[index] = {
            ...localApt[index],
            progress: progress
        };
    };

    /* ── Swipe handlers ── */
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const deltaX = e.changedTouches[0].clientX - touchStartX.current;
        const minSwipe = 60;

        if (deltaX > minSwipe && activeIndex > 0) {
            // Swipe right → previous
            setActiveIndex(prev => prev - 1);
        } else if (deltaX < -minSwipe && !!selectedOption) {
            // Swipe left → next
            handleNext();
        }
        touchStartX.current = null;
    };

    /* ── Render: General Tab (Tab 0) ── */
    const renderGeneralTab = () => {
        const progColor = getProgressColor(overallProgress);

        return (
            <Box sx={{
                height: 'calc(100vh - 56px - 70px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                ...scrollbarSx,
            }}>
                {/* Overall progress card */}
                <Box sx={{
                    mx: 2,
                    mt: 2,
                    mb: 2,
                    p: 2.5,
                    borderRadius: '18px',
                    background: GLASS_BG,
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: `1px solid ${GLASS_BORDER}`,
                    boxShadow: `${GLASS_SHADOW}, inset 0 1px 0 rgba(255,255,255,0.5)`,
                    position: 'relative',
                    overflow: 'hidden',
                    animation: `mcpf-fadeSlideUp 450ms ${EASE_ENTRANCE} both`,
                }}>
                    {/* Orange accent line at top */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: '10%',
                        right: '10%',
                        height: '3px',
                        borderRadius: '0 0 3px 3px',
                        background: `linear-gradient(90deg, transparent, ${ORANGE}, #FFC100, transparent)`,
                        opacity: 0.6,
                    }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#2d3748', letterSpacing: '-0.01em' }}>
                            <Langs str='Overall Progress' />
                        </Typography>
                        <Typography sx={{
                            fontSize: '1.2rem',
                            fontWeight: 700,
                            color: progColor,
                            letterSpacing: '-0.02em',
                            textShadow: `0 0 20px ${progColor}33`,
                        }}>
                            {overallProgress}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(parseFloat(overallProgress) || 0, 100)}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            boxShadow: `inset 0 1px 2px rgba(0,0,0,0.06)`,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: progColor,
                                borderRadius: 5,
                                boxShadow: `0 0 12px ${progColor}44, 0 2px 4px ${progColor}33`,
                                transition: `transform 800ms ${EASE_SMOOTH}`,
                            },
                        }}
                    />
                </Box>

                {/* Expand/Collapse chips */}
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    px: 2,
                    mb: 1.5,
                    animation: `mcpf-fadeSlideUp 400ms ${EASE_ENTRANCE} 100ms both`,
                }}>
                    <Chip
                        icon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Expand All" />}
                        size="small"
                        variant="outlined"
                        onClick={() => setExpandedKeys(collectAllKeys(progressNodes))}
                        sx={{
                            borderColor: ORANGE_BORDER,
                            backgroundColor: ORANGE_GLASS,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            height: 32,
                            borderRadius: '10px',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            transition: `all 200ms ${EASE_SMOOTH}`,
                            '&:active': { transform: 'scale(0.96)' },
                        }}
                    />
                    <Chip
                        icon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Collapse All" />}
                        size="small"
                        variant="outlined"
                        onClick={() => setExpandedKeys({})}
                        sx={{
                            borderColor: ORANGE_BORDER,
                            backgroundColor: ORANGE_GLASS,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            height: 32,
                            borderRadius: '10px',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            transition: `all 200ms ${EASE_SMOOTH}`,
                            '&:active': { transform: 'scale(0.96)' },
                        }}
                    />
                </Box>

                {/* Tree nodes */}
                <Box sx={{
                    mx: 0,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    pb: 1,
                }}>
                    {progressNodes.length === 0 ? (
                        <Box sx={{
                            textAlign: 'center',
                            py: 8,
                            animation: `mcpf-fadeSlideUp 500ms ${EASE_ENTRANCE} both`,
                        }}>
                            <Box sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: ORANGE_LIGHT,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 1.5,
                            }}>
                                <AssignmentOutlinedIcon sx={{ fontSize: 30, color: ORANGE, opacity: 0.5 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', color: '#9ca3af', fontWeight: 500 }}>
                                <Langs str="NO_APTITUDES_MESSAGE" />
                            </Typography>
                        </Box>
                    ) : (
                        progressNodes.map((node, idx) => (
                            <MobileProgressNode
                                key={node.key}
                                node={node}
                                depth={0}
                                expandedKeys={expandedKeys}
                                onToggle={handleToggleKey}
                                nodeIndex={idx}
                            />
                        ))
                    )}
                </Box>

                {/* Bottom safe area spacer */}
                <Box sx={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }} />
            </Box>
        );
    };

    /* ── Render: Entry Tab (Tab 1) ── */
    const renderEntryTab = () => {
        if (localApt.length === 0) {
            return (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100vh - 56px - 70px)',
                    gap: 2,
                    animation: `mcpf-fadeSlideUp 450ms ${EASE_ENTRANCE} both`,
                }}>
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '50%',
                        backgroundColor: ORANGE_LIGHT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: ORANGE_SOFT_GLOW,
                    }}>
                        <AssignmentOutlinedIcon sx={{ fontSize: 32, color: ORANGE, opacity: 0.7 }} />
                    </Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280', letterSpacing: '-0.01em' }}>
                        <Langs str="NO_APTITUDES_MESSAGE" />
                    </Typography>
                </Box>
            );
        }

        const currentApt = localApt[activeIndex] || {};
        const currentProgress = parseInt(currentApt.progress || '0', 10);
        const steps = currentApt.aspect === "theoretical"
            ? PROGRESS_STEPS_THEORETICAL
            : PROGRESS_STEPS_PRACTICAL;

        return (
            <Box
                ref={contentRef}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                sx={{
                    height: 'calc(100vh - 56px - 70px)',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    px: 2,
                    pt: 2,
                    pb: 2,
                    ...scrollbarSx,
                }}
            >
                {/* Breadcrumb */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1.5,
                    flexWrap: 'wrap',
                }}>
                    {currentApt.category_name && (
                        <Chip
                            label={currentApt.category_name}
                            size="small"
                            sx={{
                                height: 26,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: 'rgba(255, 144, 21, 0.06)',
                                color: '#78716c',
                                borderRadius: '8px',
                                border: `1px solid ${ORANGE_BORDER}`,
                                animation: `mcpf-chipFadeIn 300ms ${EASE_ENTRANCE} both`,
                            }}
                        />
                    )}
                    {currentApt.category_name && currentApt.subcategory_name && (
                        <ChevronRightIcon sx={{
                            fontSize: 16,
                            color: 'rgba(255, 144, 21, 0.3)',
                            animation: `mcpf-chipFadeIn 300ms ${EASE_ENTRANCE} 50ms both`,
                        }} />
                    )}
                    {currentApt.subcategory_name && (
                        <Chip
                            label={currentApt.subcategory_name}
                            size="small"
                            sx={{
                                height: 26,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: 'rgba(255, 144, 21, 0.06)',
                                color: '#78716c',
                                borderRadius: '8px',
                                border: `1px solid ${ORANGE_BORDER}`,
                                animation: `mcpf-chipFadeIn 300ms ${EASE_ENTRANCE} 100ms both`,
                            }}
                        />
                    )}
                </Box>

                {/* Aptitude name + description */}
                <Typography
                    key={`apt-name-${activeIndex}`}
                    sx={{
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        color: '#1a1a2e',
                        mb: 0.5,
                        lineHeight: 1.3,
                        letterSpacing: '-0.02em',
                        animation: `mcpf-fadeSlideUp 350ms ${EASE_ENTRANCE} both`,
                    }}
                >
                    {currentApt.nom}
                </Typography>
                {currentApt.description && (
                    <Typography
                        key={`apt-desc-${activeIndex}`}
                        sx={{
                            fontSize: '0.85rem',
                            fontStyle: 'italic',
                            color: '#78716c',
                            mb: 2,
                            lineHeight: 1.5,
                            animation: `mcpf-fadeSlideUp 350ms ${EASE_ENTRANCE} 50ms both`,
                        }}
                    >
                        {currentApt.description}
                    </Typography>
                )}

                {/* Aspect badge */}
                {currentApt.aspect && (
                    <Chip
                        key={`apt-aspect-${activeIndex}`}
                        label={currentApt.aspect === 'theoretical' ? 'Théorique' : 'Pratique'}
                        size="small"
                        sx={{
                            mb: 2,
                            height: 26,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            borderRadius: '8px',
                            backgroundColor: currentApt.aspect === 'theoretical'
                                ? 'rgba(99, 102, 241, 0.08)' : ORANGE_LIGHT,
                            color: currentApt.aspect === 'theoretical'
                                ? '#6366f1' : ORANGE,
                            border: currentApt.aspect === 'theoretical'
                                ? '1px solid rgba(99, 102, 241, 0.12)' : `1px solid ${ORANGE_BORDER}`,
                            animation: `mcpf-chipFadeIn 300ms ${EASE_ENTRANCE} 80ms both`,
                        }}
                    />
                )}

                {/* Progress step cards */}
                <Box
                    key={`steps-${activeIndex}`}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
                >
                    {steps.map((step, idx) => {
                        const isSelected = selectedOption === step.value;
                        const isDisabled = currentProgress > parseInt(step.value);
                        const isCompleted = currentProgress >= parseInt(step.value) && parseInt(step.value) > 0;

                        return (
                            <MobileStepCard
                                key={step.value}
                                step={step}
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                                isCompleted={isCompleted}
                                onSelect={setSelectedOption}
                                index={idx}
                            />
                        );
                    })}
                </Box>

                {/* Bottom spacer for sticky bar */}
                <Box sx={{ height: 16 }} />
            </Box>
        );
    };

    /* ── Main render ── */
    return (
        <Dialog
            fullScreen
            open={!!visible}
            onClose={handleClose}
            TransitionComponent={SlideTransition}
            sx={{
                zIndex: 1400,
                '& .MuiDialog-paper': {
                    background: 'linear-gradient(160deg, #ffffff 0%, #fafbfc 50%, #f5f3f0 100%)',
                    overflow: 'hidden',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                    }
                }
            }}
        >
            {/* ── Sticky App Bar ── */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    background: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    borderBottom: `1px solid ${ORANGE_BORDER}`,
                    color: '#1a1a2e',
                    boxShadow: '0 1px 12px rgba(255, 144, 21, 0.04)',
                }}
            >
                <Toolbar sx={{
                    justifyContent: 'space-between',
                    px: 1,
                    minHeight: '56px !important',
                }}>
                    {/* Tabs */}
                    <Tabs
                        value={activeTabIndex}
                        onChange={(e, val) => setActiveTabIndex(val)}
                        sx={{
                            minHeight: 48,
                            '& .MuiTabs-indicator': {
                                background: `linear-gradient(90deg, ${ORANGE}, #FFC100)`,
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                boxShadow: `0 0 8px ${ORANGE_GLOW}`,
                            },
                            '& .MuiTab-root': {
                                minHeight: 48,
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: '#9ca3af',
                                letterSpacing: '-0.01em',
                                gap: 0.5,
                                px: 1.5,
                                transition: `all 250ms ${EASE_SMOOTH}`,
                                '&.Mui-selected': {
                                    color: ORANGE,
                                    fontWeight: 700,
                                },
                            },
                        }}
                    >
                        <Tab
                            icon={<BoltIcon sx={{ fontSize: 16 }} />}
                            iconPosition="end"
                            label={<Langs str="general" />}
                        />
                        <Tab
                            icon={<EditIcon sx={{ fontSize: 14 }} />}
                            iconPosition="end"
                            label={<Langs str="entry" />}
                        />
                    </Tabs>

                    {/* Close button */}
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: '#9ca3af',
                            width: 40,
                            height: 40,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            border: '1px solid rgba(0,0,0,0.04)',
                            transition: `all 250ms ${EASE_SMOOTH}`,
                            '&:hover': {
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                borderColor: 'rgba(239, 68, 68, 0.12)',
                            },
                            '&:active': {
                                transform: 'scale(0.90)',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* ── Body ── */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {activeTabIndex === 0 ? renderGeneralTab() : renderEntryTab()}
            </Box>

            {/* ── Sticky Bottom Bar (Entry tab only) ── */}
            {activeTabIndex === 1 && localApt.length > 0 && (
                <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                    background: 'rgba(255, 255, 255, 0.72)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    borderTop: `1px solid rgba(255, 144, 21, 0.1)`,
                    boxShadow: '0 -4px 24px rgba(0,0,0,0.04), 0 -1px 6px rgba(255,144,21,0.03)',
                    animation: `mcpf-fadeSlideUp 400ms ${EASE_ENTRANCE} 200ms both`,
                }}>
                    {/* Previous button */}
                    <Button
                        variant="outlined"
                        disabled={activeIndex <= 0}
                        onClick={() => setActiveIndex(prev => prev - 1)}
                        startIcon={<NavigateBeforeIcon />}
                        sx={{
                            borderColor: 'rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(255,255,255,0.6)',
                            color: '#374151',
                            borderRadius: '14px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 2,
                            py: 1,
                            minHeight: 48,
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            transition: `all 250ms ${EASE_SMOOTH}`,
                            '&:active': { transform: 'scale(0.97)' },
                            '&.Mui-disabled': {
                                borderColor: 'rgba(0,0,0,0.04)',
                                color: 'rgba(0,0,0,0.18)',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                            },
                        }}
                    >
                        <Langs str="Previous" />
                    </Button>

                    {/* Counter badge */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        fontWeight: 600,
                        backgroundColor: 'rgba(255, 144, 21, 0.06)',
                        border: `1px solid ${ORANGE_BORDER}`,
                        borderRadius: '24px',
                        px: 1.75,
                        py: 0.6,
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                    }}>
                        <Box
                            key={activeIndex}
                            component="span"
                            sx={{
                                color: ORANGE,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                animation: `mcpf-counterPop 300ms ${EASE_ENTRANCE}`,
                            }}
                        >
                            {activeIndex + 1}
                        </Box>
                        <Box component="span" sx={{ opacity: 0.4 }}>/</Box>
                        <Box component="span">{localApt.length}</Box>
                    </Box>

                    {/* Next button */}
                    <Button
                        variant="contained"
                        disabled={!selectedOption}
                        onClick={handleNext}
                        endIcon={<NavigateNextIcon />}
                        sx={{
                            background: `linear-gradient(135deg, ${ORANGE}, #FFC100)`,
                            color: '#fff',
                            borderRadius: '14px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            px: 2.5,
                            py: 1,
                            minHeight: 48,
                            boxShadow: `0 4px 16px ${ORANGE_GLOW}, 0 1px 4px rgba(255,144,21,0.15)`,
                            transition: `all 250ms ${EASE_SMOOTH}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, #e8820f, #e8ae00)`,
                                boxShadow: `0 6px 20px rgba(255,144,21,0.35), 0 2px 6px rgba(255,144,21,0.2)`,
                            },
                            '&:active': { transform: 'scale(0.97)' },
                            '&.Mui-disabled': {
                                background: 'rgba(0,0,0,0.06)',
                                color: 'rgba(0,0,0,0.22)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Langs str="Next" />
                    </Button>
                </Box>
            )}
        </Dialog>
    );
};

export default MobileCollabProjectForm;
